import type { MutationTree } from 'vuex'
import type { Subscription, SubscriptionSKU } from '@/types/subscriptions'
import type {
  SubscriptionsState,
  EnrichedSubscription,
  ModelQtyRemainingPayload,
  ModelQtyUsedPayload
} from './types'
import { SUBSCRIPTIONS_MUTATIONS } from './types'
import { deviceModels } from './utils'

interface ISubscriptionsMutations extends MutationTree<SubscriptionsState> {
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_SKU_METADATA](
    state: SubscriptionsState,
    SKUMetadata: Record<string, SubscriptionSKU>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_SUBSCRIPTIONS](
    state: SubscriptionsState,
    subscriptions: Subscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_GENERIC_TSI_LINK](
    state: SubscriptionsState,
    subscriptions: EnrichedSubscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE](
    state: SubscriptionsState,
    subscriptions: EnrichedSubscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC](
    state: SubscriptionsState,
    subscriptions: EnrichedSubscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM](
    state: SubscriptionsState,
    subscriptions: EnrichedSubscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DATA_SERVICES](
    state: SubscriptionsState,
    subscriptions: EnrichedSubscription[]
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_WEBSITE](
    state: SubscriptionsState,
    devices: Record<string, string>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_GENERIC](
    state: SubscriptionsState,
    devices: Record<string, string>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_BASIC](
    state: SubscriptionsState,
    devices: Record<string, string>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_PREMIUM](
    state: SubscriptionsState,
    devices: Record<string, string>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_DATA_SERVICES](
    state: SubscriptionsState,
    devices: Record<string, string>
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED](
    state: SubscriptionsState,
    payload: ModelQtyUsedPayload
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING](
    state: SubscriptionsState,
    payload: ModelQtyRemainingPayload
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING](
    state: SubscriptionsState,
    payload: ModelQtyRemainingPayload
  ): void
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING](
    state: SubscriptionsState,
    payload: ModelQtyRemainingPayload
  ): void
}

const mutations: ISubscriptionsMutations = {
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_SKU_METADATA](state, SKUMetadata) {
    state.SKUMetadata = SKUMetadata
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_SUBSCRIPTIONS](state, subscriptions) {
    state.subscriptions = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_GENERIC_TSI_LINK](state, subscriptions) {
    state.genericTSILink = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE](state, subscriptions) {
    state.website = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC](state, subscriptions) {
    state.basic = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM](state, subscriptions) {
    state.premium = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DATA_SERVICES](state, subscriptions) {
    state.dataServices = subscriptions
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_WEBSITE](state, devices) {
    state.deviceWebsite = devices
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_GENERIC](state, devices) {
    state.deviceGeneric = devices
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_BASIC](state, devices) {
    state.deviceBasic = devices
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_PREMIUM](state, devices) {
    state.devicePremium = devices
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_DEVICE_DATA_SERVICES](state, devices) {
    state.deviceDataServices = devices
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_USED](state, { qtyUsed, model }) {
    if (model === deviceModels.BLUESKY) {
      state.websiteQtyUsedBlueSky = qtyUsed
    } else if (model === deviceModels.BLUESKY_GAS) {
      state.websiteQtyUsedBlueSkyGas = qtyUsed
    } else if (model === deviceModels.AIRASSURE) {
      state.websiteQtyUsedAirAssure = qtyUsed
    }
    //do nothing if model unknown
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_WEBSITE_QTY_REMAINING](state, { qtyRemaining, model }) {
    if (model === deviceModels.BLUESKY) {
      state.websiteQtyRemainingBlueSky = qtyRemaining
    } else if (model === deviceModels.BLUESKY_GAS) {
      state.websiteQtyRemainingBlueSkyGas = qtyRemaining
    } else if (model === deviceModels.AIRASSURE) {
      state.websiteQtyRemainingAirAssure = qtyRemaining
    }
    //do nothing if model unknown
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_BASIC_QTY_REMAINING](state, { qtyRemaining, model }) {
    if (model === deviceModels.BLUESKY) {
      state.basicQtyRemainingBlueSky = qtyRemaining
    } else if (model === deviceModels.AIRASSURE) {
      state.basicQtyRemainingAirAssure = qtyRemaining
      return
    }
    //do nothing if model unknown
  },
  [SUBSCRIPTIONS_MUTATIONS.UPDATE_PREMIUM_QTY_REMAINING](state, { qtyRemaining, model }) {
    if (model === deviceModels.BLUESKY) {
      state.premiumQtyRemainingBlueSky = qtyRemaining
    } else if (model === deviceModels.BLUESKY_GAS) {
      state.premiumQtyRemainingBlueSkyGas = qtyRemaining
      return
    } else if (model === deviceModels.AIRASSURE) {
      state.premiumQtyRemainingAirAssure = qtyRemaining
      return
    }
    //do nothing if model unknown
  }
}

export default mutations
